import {SxProps, Theme} from '@mui/material'
import {alpha} from '@mui/material/styles'
import {itIT} from '@mui/x-date-pickers/locales'
import {CONTROL_HEIGHT, NEUTRAL_SCALE} from 'src/theme-new/tokens'

// Shared look of DatePickerNew / TimePickerNew: the input field and the popup (calendar, time
// columns, mobile dialog) are styled once here so the two pickers stay visually identical.

// MUI X ships "Cancellare" as the Italian cancel label of the mobile dialog: fixed here.
export const PICKER_LOCALE_TEXT = {
  ...itIT.components.MuiLocalizationProvider.defaultProps.localeText,
  cancelButtonLabel: 'Annulla'
}

export const pickerFieldSx = (error?: boolean): SxProps<Theme> => (theme) => ({
  '& .MuiOutlinedInput-root': {
    height: CONTROL_HEIGHT,
    borderRadius: '8px',
    backgroundColor: theme.palette.background.paper,
    fontVariantNumeric: 'tabular-nums',
    transition: theme.transitions.create(['box-shadow'], {duration: 150}),
    '& fieldset': {borderColor: error ? theme.palette.error.main : NEUTRAL_SCALE[200]},
    '&:hover fieldset': {borderColor: error ? theme.palette.error.main : NEUTRAL_SCALE[300]},
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.12)}`
    },
    '&.Mui-focused fieldset': {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
      borderWidth: 1
    },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
      color: NEUTRAL_SCALE[500],
      '& svg': {fontSize: 20}
    },
    '&:hover .MuiInputAdornment-root .MuiIconButton-root, &.Mui-focused .MuiInputAdornment-root .MuiIconButton-root': {
      color: theme.palette.primary.main
    }
  },
  '& .Mui-disabled': {opacity: 0.6}
})

const paperSx = (theme: Theme, radius: number) => ({
  borderRadius: `${radius}px`,
  border: `1px solid ${NEUTRAL_SCALE[200]}`,
  boxShadow: `0 12px 32px ${alpha(NEUTRAL_SCALE[900], 0.12)}`,
  mt: 0.5,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper
})

// Popup content: calendar days, month/year header, time columns.
export const pickerLayoutSx: SxProps<Theme> = (theme) => {
  const primary = theme.palette.primary.main
  const selected = {
    backgroundColor: primary,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    '&:hover, &:focus': {backgroundColor: primary}
  }
  return {
    '& .MuiPickersCalendarHeader-label': {
      textTransform: 'capitalize',
      fontWeight: 600,
      color: NEUTRAL_SCALE[900]
    },
    '& .MuiPickersArrowSwitcher-button, & .MuiPickersCalendarHeader-switchViewButton': {
      color: NEUTRAL_SCALE[700]
    },
    '& .MuiDayCalendar-weekDayLabel': {
      color: NEUTRAL_SCALE[500],
      fontWeight: 600,
      textTransform: 'uppercase'
    },
    '& .MuiPickersDay-root': {
      borderRadius: '10px',
      fontWeight: 500,
      color: NEUTRAL_SCALE[900],
      '&:hover': {backgroundColor: alpha(primary, 0.08)},
      '&.Mui-selected': selected,
      '&.Mui-disabled:not(.Mui-selected)': {color: NEUTRAL_SCALE[300]}
    },
    '& .MuiPickersDay-today:not(.Mui-selected)': {
      border: `1px solid ${primary}`,
      color: primary
    },
    '& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton': {
      borderRadius: '10px',
      '&:hover': {backgroundColor: alpha(primary, 0.08)},
      '&.Mui-selected': selected
    },
    '& .MuiMultiSectionDigitalClockSection-root': {
      borderLeftColor: NEUTRAL_SCALE[100]
    },
    '& .MuiMultiSectionDigitalClockSection-item, & .MuiDigitalClock-item': {
      borderRadius: '8px',
      mx: 0.5,
      fontVariantNumeric: 'tabular-nums',
      '&:hover': {backgroundColor: alpha(primary, 0.08)},
      '&.Mui-selected': selected
    },
    '& .MuiPickersLayout-actionBar .MuiButton-root': {
      borderRadius: '8px',
      fontWeight: 600,
      textTransform: 'none'
    }
  }
}

export const pickerPopupSlotProps = (zIndex: number) => ({
  popper: {sx: {zIndex}},
  dialog: {sx: {zIndex}},
  desktopPaper: {sx: (theme: Theme) => paperSx(theme, 12)},
  mobilePaper: {sx: (theme: Theme) => ({...paperSx(theme, 16), zIndex})},
  layout: {sx: pickerLayoutSx}
})
